import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Input, Tag, Empty, Spin } from "antd";
import {
    SearchOutlined,
    HistoryOutlined,
    RightOutlined,
    AuditOutlined,
    FileTextOutlined,
    CheckSquareOutlined,
    UserOutlined,
    AppstoreOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

import { useMaxWidth } from "../../hooks/useMaxWidth";
import { useCommandPalette } from "./useCommandPalette";
import CommandPaletteResultGroup from "./CommandPaletteResultGroup";
import {
    ENTITY_LABELS,
} from "./getSearchableEntityTypes";
import type {
    SearchEntityType,
    SearchResultItem,
} from "../../services/apis/search.api";
import "./CommandPalette.scss";

const QUICK_LINKS: Array<{
    icon: React.ReactNode;
    label: string;
    url: string;
    requires?: SearchEntityType;
}> = [
        { icon: <AuditOutlined />, label: "My Audits", url: "/audits" },
        {
            icon: <FileTextOutlined />,
            label: "New Assessment",
            url: "/assessments/create",
            requires: "assessments",
        },
        {
            icon: <CheckSquareOutlined />,
            label: "Task Manager",
            url: "/task-and-goals/tasks",
            requires: "tasks",
        },
        {
            icon: <AppstoreOutlined />,
            label: "Audit Categories",
            url: "/audit-categories",
            requires: "auditCategories",
        },
        {
            icon: <UserOutlined />,
            label: "My Profile",
            url: "/profile",
        },
    ];

const TYPE_ORDER: SearchEntityType[] = [
    "people",
    "students",
    "audits",
    "oneTimeAudits",
    "assessments",
    "questionBanks",
    "evaluations",
    "anecdotals",
    "tasks",
    "templates",
    "groups",
    "departments",
    "auditCategories",
    "parameters",
    "criteria",
    "imageVerification",
    "posts",
    "notices",
    "tickets",
    "organizations",
];

const CommandPalette: React.FC = () => {
    const isMobile = useMaxWidth(767);
    const navigate = useNavigate();

    const {
        open,
        query,
        loading,
        results,
        totals,
        recent,
        allowedTypes,
        setQuery,
        close,
        selectResult,
        replayRecent,
        clearRecentSearches,
    } = useCommandPalette();

    const inputRef = useRef<any>(null);

    // Build a flat list of visible rows (for keyboard nav).
    const flatRows = useMemo(() => {
        const out: Array<{ item: SearchResultItem; type: SearchEntityType }> = [];
        if (!results) return out;
        TYPE_ORDER.forEach((t) => {
            if (!allowedTypes.includes(t)) return;
            (results[t] || []).forEach((item) => {
                out.push({ item, type: t });
            });
        });
        return out;
    }, [results, allowedTypes]);

    const [activeIndex, setActiveIndex] = useState(0);

    // Reset active index whenever results change
    useEffect(() => {
        setActiveIndex(0);
    }, [results]);

    // Autofocus input on open
    useEffect(() => {
        if (open) {
            setTimeout(() => inputRef.current?.focus?.(), 30);
        }
    }, [open]);

    // Keyboard navigation - arrow/enter/esc
    useEffect(() => {
        if (!open) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                e.preventDefault();
                close();
                return;
            }
            if (flatRows.length === 0) return;
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setActiveIndex((i) => (i + 1) % flatRows.length);
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setActiveIndex((i) =>
                    (i - 1 + flatRows.length) % flatRows.length
                );
            } else if (e.key === "Enter") {
                e.preventDefault();
                const row = flatRows[activeIndex];
                if (row) selectResult(row.item, row.type);
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [open, flatRows, activeIndex, close, selectResult]);

    // Keep the keyboard-focused row visible when navigating past the viewport.
    useEffect(() => {
        if (!open) return;
        const el = document.querySelector(
            `[data-index="${activeIndex}"]`
        );
        el?.scrollIntoView({ block: "nearest" });
    }, [activeIndex, open]);

    const hasQuery = (query || "").trim().length >= 2;
    const anyResults =
        !!results &&
        TYPE_ORDER.some((t) => allowedTypes.includes(t) && (results[t]?.length || 0) > 0);

    // Visible quick links filtered by role
    const quickLinks = QUICK_LINKS.filter(
        (q) => !q.requires || allowedTypes.includes(q.requires)
    );

    const shortcutHint = useMemo(() => {
        const isMac = typeof navigator !== "undefined" &&
            /Mac|iPhone|iPad/.test(navigator.platform || "");
        return isMac ? "⌘K" : "Ctrl K";
    }, []);

    return (
        <Modal
            open={open}
            onCancel={close}
            footer={null}
            closable={false}
            destroyOnHidden
            maskClosable
            centered={!isMobile}
            width={isMobile ? "100vw" : 720}
            className={`cmdp-modal${isMobile ? " cmdp-modal--mobile" : ""}`}
            styles={{
                body: { padding: 0 },
                mask: { backdropFilter: "blur(2px)" },
            }}
            style={
                isMobile
                    ? { top: 0, margin: 0, maxWidth: "100vw", padding: 0 }
                    : undefined
            }
        >
            <div className={`cmdp${isMobile ? " cmdp--mobile" : ""}`}>
                <div className="cmdp__header">
                    <Input
                        ref={inputRef}
                        prefix={<SearchOutlined className="cmdp__header-icon" />}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search audits, assessments, templates, tasks, people…"
                        className="cmdp__input"
                        variant="borderless"
                        suffix={
                            !isMobile ? (
                                <Tag className="cmdp__shortcut">
                                    {shortcutHint}
                                </Tag>
                            ) : null
                        }
                        size="large"
                        allowClear
                    />
                </div>

                <div className="cmdp__body">
                    {!hasQuery && (
                        <>
                            {recent.length > 0 && (
                                <div className="cmdp__group">
                                    <div className="cmdp__group-header">
                                        <span className="cmdp__group-title">
                                            <HistoryOutlined /> Recent
                                        </span>
                                        <button
                                            type="button"
                                            className="cmdp__group-clear"
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                clearRecentSearches();
                                            }}
                                        >
                                            Clear
                                        </button>
                                    </div>
                                    <ul className="cmdp__group-list">
                                        {recent.map((r, i) => (
                                            <li
                                                key={`${r.url}-${i}`}
                                                className="cmdp__row"
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    replayRecent(r);
                                                }}
                                            >
                                                <span className="cmdp__row-icon">
                                                    <HistoryOutlined />
                                                </span>
                                                <span className="cmdp__row-body">
                                                    <span className="cmdp__row-title">
                                                        {r.title}
                                                    </span>
                                                    <span className="cmdp__row-sub">
                                                        {ENTITY_LABELS[r.type]}
                                                    </span>
                                                </span>
                                                <RightOutlined className="cmdp__row-arrow" />
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {quickLinks.length > 0 && (
                                <div className="cmdp__group">
                                    <div className="cmdp__group-header">
                                        <span className="cmdp__group-title">
                                            Quick Links
                                        </span>
                                    </div>
                                    <ul className="cmdp__group-list">
                                        {quickLinks.map((q) => (
                                            <li
                                                key={q.label}
                                                className="cmdp__row"
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    navigate(q.url);
                                                    close();
                                                }}
                                            >
                                                <span className="cmdp__row-icon">
                                                    {q.icon}
                                                </span>
                                                <span className="cmdp__row-body">
                                                    <span className="cmdp__row-title">
                                                        {q.label}
                                                    </span>
                                                </span>
                                                <RightOutlined className="cmdp__row-arrow" />
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {recent.length === 0 && quickLinks.length === 0 && (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description="Start typing to search"
                                    style={{ padding: 32 }}
                                />
                            )}
                        </>
                    )}

                    {hasQuery && loading && (
                        <div className="cmdp__loading">
                            <Spin size="small" /> Searching…
                        </div>
                    )}

                    {hasQuery && !loading && !anyResults && (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                                <span>
                                    No results for <strong>“{query}”</strong>
                                </span>
                            }
                            style={{ padding: 32 }}
                        />
                    )}

                    {hasQuery && !loading && anyResults && results && totals && (
                        <>
                            {(() => {
                                let runningIndex = 0;
                                return TYPE_ORDER.map((t) => {
                                    if (!allowedTypes.includes(t)) return null;
                                    const items = results[t] || [];
                                    if (items.length === 0) return null;
                                    const base = runningIndex;
                                    runningIndex += items.length;
                                    return (
                                        <CommandPaletteResultGroup
                                            key={t}
                                            type={t}
                                            items={items}
                                            total={totals[t] || items.length}
                                            activeIndex={activeIndex}
                                            baseIndex={base}
                                            query={query}
                                            onSelect={selectResult}
                                            onClose={close}
                                        />
                                    );
                                });
                            })()}
                        </>
                    )}
                </div>

                {!isMobile && (
                    <div className="cmdp__footer">
                        <span className="cmdp__footer-hint">
                            <kbd>↑</kbd>
                            <kbd>↓</kbd> navigate
                        </span>
                        <span className="cmdp__footer-hint">
                            <kbd>↵</kbd> open
                        </span>
                        <span className="cmdp__footer-hint">
                            <kbd>esc</kbd> close
                        </span>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default CommandPalette;
